import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
	Box,
	CircularProgress,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
} from "@mui/material";
import type { RollLayoutDocument } from "../models/RollLayout";

interface Props {
	documents: RollLayoutDocument[];
	isLoading: boolean;
	isError: boolean;
	selectedPath: string | null;
	onSelect: (document: RollLayoutDocument) => void;
}

const EmptyState = ({
	icon,
	message,
	color,
}: {
	icon: React.ReactNode;
	message: string;
	color?: string;
}) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 1,
			p: 4,
			color: color ?? "text.secondary",
			textAlign: "center",
		}}
	>
		{icon}
		<Typography variant="body2">{message}</Typography>
	</Box>
);

export const DocumentsList = ({
	documents,
	isLoading,
	isError,
	selectedPath,
	onSelect,
}: Props) => {
	if (isLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (isError) {
		return (
			<EmptyState
				icon={<ErrorOutlineIcon sx={{ fontSize: 36 }} />}
				message="Error loading documents."
				color="error.main"
			/>
		);
	}

	if (documents.length === 0) {
		return (
			<EmptyState
				icon={<FolderOpenIcon sx={{ fontSize: 36 }} />}
				message="No documents found for this stand."
			/>
		);
	}

	return (
		<List dense disablePadding>
			{documents.map((doc) => (
				<ListItemButton
					key={doc.DocumentPath}
					selected={doc.DocumentPath === selectedPath}
					onClick={() => onSelect(doc)}
				>
					<ListItemIcon sx={{ minWidth: 32 }}>
						<PictureAsPdfIcon color="error" fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={doc.DocumentName}
						primaryTypographyProps={{ variant: "body2" }}
					/>
				</ListItemButton>
			))}
		</List>
	);
};
